'use client';

import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

type Props = {
  src: string;
  poster?: string;
  muted?: boolean;
  active?: boolean;
  style?: React.CSSProperties;
  onOrientation?: (isLandscape: boolean) => void;
  isLandscape?: boolean | null;
};

const VideoPlayerWeb: React.FC<Props> = ({
  src,
  poster,
  muted = true,
  active = true,
  style = {},
  onOrientation,
  isLandscape,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [ready, setReady] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [userPaused, setUserPaused] = useState(false);

  const emitOrientation = (v: HTMLVideoElement) => {
    if (!v.videoWidth || !v.videoHeight) return;
    onOrientation?.(v.videoWidth > v.videoHeight);
  };

  /* ---------------- ICONS ---------------- */

  const PlayIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#bcb4b4">
      <path d="M7 5v14l11-7z" />
    </svg>
  );

  const PauseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#bcb4b4">
      <rect x="7.2" y="5" width="3.6" height="14" rx="1.2" />
      <rect x="13.2" y="5" width="3.6" height="14" rx="1.2" />
    </svg>
  );

  const SpeakerOnIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15 9a4 4 0 0 1 0 6" />
      <path d="M18 7a7 7 0 0 1 0 10" />
    </svg>
  );

  const SpeakerOffIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="2">
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <line x1="23" y1="9" x2="17" y2="15" />
      <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  );

  /* ---------------- Video attach ---------------- */

  useEffect(() => {
  const video = videoRef.current;
  if (!video || !src) return;

  let hls: Hls | null = null;

  const isHlsSource =
    src.includes('.m3u8') ||
    src.includes('/hls/') ||
    src.includes('/playlist/');

  if (isHlsSource) {
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.load();
    } else if (Hls.isSupported()) {
      hls = new Hls({ startLevel: 0, enableWorker: true });
      hls.loadSource(src);
      hls.attachMedia(video);
    }
  } else {
    video.src = src;
    video.load();
  }

  return () => {
    if (hls) hls.destroy();
  };
}, [src]);
  /* ---------------- autoplay ---------------- */

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !active) return;

    if (!userPaused) {
      v.play().catch(() => {});
    }
  }, [active, userPaused]);

  /* ---------------- progress ---------------- */

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleScrub = (value: number) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    v.currentTime = (v.duration * value) / 100;
  };

  /* ---------------- time ---------------- */

  const formatTime = (sec: number) => {
    if (!sec || isNaN(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  /* ---------------- volume ---------------- */

  const handleVolume = (value: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = value;
    setVolume(value);
  };

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = isMuted;
  }, [isMuted]);

  /* ---------------- play/pause ---------------- */

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;

    if (!v.paused && !v.ended) {
      v.pause();
      setUserPaused(true);
    } else {
      v.play().catch(() => {});
      setUserPaused(false);
    }
  };

  /* ---------------- render ---------------- */

  return (
   <div
  style={{
    position: 'relative',
    display: 'inline-block',
    overflow: 'hidden',
    ...style,   // ← THIS is key
  }}

      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
    >
      <video
        ref={videoRef}
        poster={poster}
        muted={isMuted}
        playsInline
        preload="auto"
        controls={false}
        onPointerDown={togglePlay}
        onLoadedMetadata={(e) => {
          setReady(true);
          emitOrientation(e.currentTarget);
        }}
        onLoadedData={(e) => emitOrientation(e.currentTarget)}
        onCanPlay={(e) => emitOrientation(e.currentTarget)}
        onTimeUpdate={onTimeUpdate}
        onPlay={() => {
          setIsPlaying(true);
          setUserPaused(false);
        }}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          const v = videoRef.current;
          if (!v) return;
          v.currentTime = 0;
          v.play().catch(() => {});
        }}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          ...style,
        }}
      />

      {/* CONTROLS */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          background: 'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(6px)',
          opacity: showControls ? 1 : 0,
          transition: 'opacity 200ms ease',
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', width: '100%', padding: isLandscape ? "7px 14px" : "5px 8px" }}>
          <button onPointerDown={(e) => { e.stopPropagation(); togglePlay(); }} style={controlBtn}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </button>

          {/* CENTER — MAIN SCRUBBER */}
<div
  style={{
    flex: "0.9 1 0",      // grow, but don't force max expansion
    display: "flex",
    alignItems: "center",
    padding: "0 8px",   // 👈 smaller side padding
    marginRight: 6,     // 👈 keeps distance from time label
  }}
>
  <input
    type="range"
    min={0}
    max={100}
    value={progress}
    onChange={(e) => handleScrub(Number(e.target.value))}
    className="custom-scrubber"
    style={{
      width: "100%",
      height: 3,
      background: `linear-gradient(
        to right,
        #d8cece 0%,
        #d8cece ${progress}%,
        #555 ${progress}%,
        #555 100%
      )`,
    }}
  />
</div>


          <span style={{ fontSize: 11, color: '#bbb', marginRight: 7 }}>
            {formatTime(videoRef.current?.currentTime || 0)}/
            {formatTime(videoRef.current?.duration || 0)}
          </span>

          <button onClick={() => setIsMuted(!isMuted)} style={controlBtn}>
            {isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
          </button>

  <input
  type="range"
  min={0}
  max={1}
  step={0.05}
  value={isMuted ? 0 : volume}
  onChange={(e) => {
    const val = Number(e.target.value);
    setIsMuted(val === 0);
    handleVolume(val);
  }}
  className="volume-slider"
  style={{
    width: isLandscape ? 85 : 65,
    height: 2,
    background: `linear-gradient(
      to right,
      #bcb3b3 0%,
      #d8cece ${(isMuted ? 0 : volume) * 100}%,
      #666 ${(isMuted ? 0 : volume) * 100}%,
      #666 100%
    )`,
  }}
/>
        </div>
      </div>
    </div>
  );
};

const controlBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#bcb4b4',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 24,
  padding: 0,
};

export default VideoPlayerWeb;